import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Panel, TreeFilterBar, TreeView } from '@/shared/ui';
import { DetailZoneOutlet } from '@/shared/navigation';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { useDetailZone } from '@/shared/navigation/react/useDetailZone';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useGetVenuaPlannerTree } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type { PlanningTransportFilter } from '@/api/generated/schemas';
import { venuaPlannerAgenda } from './venuaPlannerAgenda';
import { projectFormAgenda } from './project';
import {
  VENUA_PLANNER_ENTITY_TYPES,
  buildVenuaPlannerTreeData,
  collectTreeTermDates,
  collectTreeTermIds,
  expandToNode,
  findNearestTermId,
} from './venuaPlannerTreeModel';
import {
  buildVenuaPlannerTreeActions,
  navigateVenuaPlannerTreeNode,
  useVenuaPlannerTreeNavigation,
} from './venuaPlannerTreeShared';
import {
  CalendarWorkspace,
  toCalendarTransportFilter,
  type CalendarAxis,
  type CalendarScrollToTerm,
} from './calendar';

interface VenuaPlannerShellPageProps {
  layerId: string;
}

/**
 * Hlavní obsah root venua-planner agendy — vlevo čtyřúrovňový strom
 * (`Project → VenuaPlannerProduct → ProductSerie → TermGroup`) nad read modelem `GetPlanningTree`,
 * vpravo kalendář termínů.
 *
 * Kalendář drží detail zónu agendy (`DetailZoneOutlet`) a je připojený pořád. Výběr řádku otevře
 * detail jako okno NAD ním (`navigateVenuaPlannerTreeNode` → `openDetail`); po zavření křížkem je
 * kalendář zase vidět přesně tam, kde uživatel přestal (scroll drží prohlížeč, nic se neodmountuje).
 *
 * Checkboxy u řádků zobrazují/skrývají termíny v kalendáři, akce „Ukázat v kalendáři" zavře
 * otevřený detail, přesadí kotvu kalendáře na měsíc termínu, odscrolluje na jeho pruh a ten pak
 * pulzem zvýrazní. Pluska u projektu/produktu zakládají produkt/seriál, „+" u seriálu otevře
 * detail seriálu rovnou s dialogem „Přidat termíny".
 */
export function VenuaPlannerShellPage({ layerId }: VenuaPlannerShellPageProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const { state } = useAgendaParams(venuaPlannerAgenda, layerId);
  const agendaState = useAgendaState(venuaPlannerAgenda, layerId);
  const nav = useVenuaPlannerTreeNavigation();
  // Detail otevřený nad kalendářem — list smí číst detail zónu (viz `useDetailZone`).
  const isDetailOpen = useDetailZone() !== null;

  const transportType = state.transportType ?? null;
  const includeHistory = state.includeHistory ?? false;
  const transport = toCalendarTransportFilter(transportType);

  const { data, isLoading, isError, error } = useGetVenuaPlannerTree({
    ...(transportType ? { transportType } : {}),
    ...(includeHistory ? { includeHistory: true } : {}),
  });

  // Zaškrtnuté termíny = lokální stav (množina UUID je příliš velká pro URL).
  // První načtení stromu zaškrtne vše; později se nově vzniklé termíny automaticky přidávají.
  const [checkedTermIds, setCheckedTermIds] = useState<ReadonlySet<string>>(new Set());
  const knownTermIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!data) return;
    const allTermIds = collectTreeTermIds(data);
    const newIds = allTermIds.filter((id) => !knownTermIdsRef.current.has(id));
    if (newIds.length === 0) return;
    knownTermIdsRef.current = new Set(allTermIds);
    setCheckedTermIds((prev) => {
      const next = new Set(prev);
      for (const id of newIds) next.add(id);
      return next;
    });
  }, [data]);

  // Příkaz „odscrolluj na termín" pro workspace; nonce odliší opakovaný klik na týž termín.
  const [scrollToTerm, setScrollToTerm] = useState<CalendarScrollToTerm | null>(null);
  const termNonceRef = useRef(0);

  // Akce nad listem (stejně jako výběr ve stromu) → detail vpravo, ne vrstva nad agendou.
  const openCreateProject = useCallback(() => {
    void navigator.openDetail(projectFormAgenda, {});
  }, [navigator]);

  useEffect(() => {
    if (!state.activeNodeId && data?.projects[0]) {
      agendaState.patch({ activeNodeId: data.projects[0].projectId });
    }
  }, [agendaState, data, state.activeNodeId]);

  const treeData = useMemo(
    () =>
      buildVenuaPlannerTreeData(data ?? { projects: [] }, {
        project: t('tree.types.project'),
        product: t('tree.types.product'),
        serie: t('tree.types.serie'),
        term: t('tree.types.term'),
      }),
    [data, t]
  );

  const termDatesById = useMemo(() => collectTreeTermDates(data ?? { projects: [] }), [data]);

  // „+" u seriálu otevře detail seriálu rovnou s dialogem „Přidat termíny" (productId z rodiče uzlu).
  const handleAddTerms = useCallback(
    (serieId: string) => {
      const productId = treeData.nodesById[serieId]?.parentId;
      if (productId) nav.addSerieTerms(productId, serieId);
    },
    [nav, treeData]
  );

  /**
   * „Ukázat v kalendáři": zavře detail otevřený nad kalendářem (close guardy se zeptají samy)
   * a pošle kalendáři příkaz odscrollovat na nejbližší termín uzlu. Skrytý termín se přitom
   * zviditelní, jinak by nebylo kam scrollovat.
   */
  const handleLocateInCalendar = useCallback(
    (nodeId: string) => {
      const termId = findNearestTermId(treeData, nodeId, termDatesById, checkedTermIds, new Date());
      if (!termId) return;
      void navigator.closeTo(layerId);
      setCheckedTermIds((prev) => {
        if (prev.has(termId)) return prev;
        const next = new Set(prev);
        next.add(termId);
        return next;
      });
      termNonceRef.current += 1;
      // Datum ze stromu jde s příkazem: kalendář podle něj přesadí kotvu i na termín, který ještě
      // nemá načtený měsíc.
      setScrollToTerm({ termId, nonce: termNonceRef.current, date: termDatesById.get(termId) });
    },
    [treeData, termDatesById, checkedTermIds, navigator, layerId]
  );

  const treeDataWithActions = useMemo(
    () => ({
      ...treeData,
      actionsByType: buildVenuaPlannerTreeActions(
        nav,
        {
          locate: t('actions.locateInCalendar'),
          addProduct: t('actions.addProduct'),
          addSerie: t('actions.addSerie'),
          addTerms: t('actions.addTerms'),
        },
        { onLocate: handleLocateInCalendar, onAddTerms: handleAddTerms }
      ),
    }),
    [treeData, nav, t, handleLocateInCalendar, handleAddTerms]
  );

  const defaultExpandedIds = useMemo(() => {
    const projectIds = data?.projects.map((project) => project.projectId) ?? [];
    if (state.activeNodeId) {
      return [...expandToNode(treeData, state.activeNodeId, projectIds)];
    }
    return projectIds;
  }, [data, state.activeNodeId, treeData]);

  const handleNodeActivate = useCallback(
    (nodeId: string) => {
      agendaState.patch({ activeNodeId: nodeId });
      // Výběr uzlu otevře jeho detail jako okno nad kalendářem; kalendář zůstává pod ním.
      navigateVenuaPlannerTreeNode(nav, treeData.nodesById, nodeId);
    },
    [agendaState, nav, treeData]
  );

  // Klik na akci řádku („+" / lupa) jen vybere (zvýrazní) — vlastní akci řeší tlačítko samo.
  const handleNodeSelect = useCallback(
    (nodeId: string) => agendaState.patch({ activeNodeId: nodeId }),
    [agendaState]
  );

  const handleTransportTypeChange = useCallback(
    (value: PlanningTransportFilter | null) => {
      if (value === null) {
        agendaState.remove('transportType');
      } else {
        agendaState.patch({ transportType: value });
      }
    },
    [agendaState]
  );

  const handleIncludeHistoryChange = useCallback(
    (value: boolean) => {
      if (value) {
        agendaState.patch({ includeHistory: true });
      } else {
        agendaState.remove('includeHistory');
      }
    },
    [agendaState]
  );

  const handleVisibleMonthChange = useCallback(
    (month: string) => agendaState.patch({ month }),
    [agendaState]
  );

  const handleOpenTerm = useCallback(
    (termId: string) => agendaState.patch({ termId }),
    [agendaState]
  );

  const handleCloseTerm = useCallback(() => agendaState.remove('termId'), [agendaState]);

  // Osa a čtení časů dopravy jsou deep-linkovatelné pohledy kalendáře — výchozí hodnota se do URL
  // nezapisuje, aby se odkaz na agendu zbytečně nenafukoval.
  const calendarAxis: CalendarAxis = state.axis ?? 'weeks';
  const calendarRealTime = state.timeMode === 'real';

  const handleAxisChange = useCallback(
    (value: CalendarAxis) => {
      if (value === 'weeks') {
        agendaState.remove('axis');
      } else {
        agendaState.patch({ axis: value });
      }
    },
    [agendaState]
  );

  const handleRealTimeChange = useCallback(
    (value: boolean) => {
      if (value) {
        agendaState.patch({ timeMode: 'real' });
      } else {
        agendaState.remove('timeMode');
      }
    },
    [agendaState]
  );

  return (
    <div className="bg-background h-full min-h-0 p-4" data-testid="venua-planner-shell">
      <Panel
        title={t('tree.title')}
        actions={
          <Button
            size="sm"
            onClick={openCreateProject}
            data-testid="venua-planner-action-add-project"
          >
            {t('actions.addProject')}
          </Button>
        }
        className="min-h-0"
      >
        <div className="flex h-full min-h-0 flex-col gap-2">
          <TreeFilterBar
            transportType={transportType}
            includeHistory={includeHistory}
            onTransportTypeChange={handleTransportTypeChange}
            onIncludeHistoryChange={handleIncludeHistoryChange}
            labels={{
              toolbar: t('filterBar.label'),
              transport: t('filterBar.transportType.label'),
              air: t('filterBar.transportType.air'),
              bus: t('filterBar.transportType.bus'),
              history: t('filterBar.history.label'),
              historyShownTitle: t('filterBar.history.shownTitle'),
              historyHiddenTitle: t('filterBar.history.hiddenTitle'),
            }}
          />
          {isLoading ? (
            <p className="text-muted-foreground text-sm">{t('states.loading')}</p>
          ) : isError ? (
            <Alert severity="error" title={t('states.errorTitle')}>
              {error instanceof Error ? error.message : t('states.errorDescription')}
            </Alert>
          ) : (
            <TreeView
              className="min-h-0 flex-1"
              data={treeDataWithActions}
              activeNodeId={state.activeNodeId ?? null}
              onNodeActivate={handleNodeActivate}
              onNodeSelect={handleNodeSelect}
              defaultExpandedIds={defaultExpandedIds}
              levelTypeIds={VENUA_PLANNER_ENTITY_TYPES}
              checkable
              checkedIds={checkedTermIds}
              onCheckedChange={setCheckedTermIds}
            />
          )}
        </div>
      </Panel>

      {/* Kalendář drží detail zónu agendy; otevřený detail ho jen překryje (scroll drží prohlížeč). */}
      <DetailZoneOutlet>
        <CalendarWorkspace
          tree={data}
          transport={transport}
          checkedTermIds={checkedTermIds}
          scrollToTerm={scrollToTerm}
          month={state.month}
          onVisibleMonthChange={handleVisibleMonthChange}
          axis={calendarAxis}
          onAxisChange={handleAxisChange}
          realTime={calendarRealTime}
          onRealTimeChange={handleRealTimeChange}
          termId={state.termId}
          onOpenTerm={handleOpenTerm}
          onCloseTerm={handleCloseTerm}
          covered={isDetailOpen}
        />
      </DetailZoneOutlet>
    </div>
  );
}
